import React from 'react';
import AudioPlayer from '../ui/AudioPlayer';
import { INTERFACE_TEXT } from '../../constants/interfaceText';
import type { Message } from '../../hooks/useLessonState';
import type { ContextLineView } from '../../turncard/viewModel';
import styles from './ContextRow.module.css';

interface ContextRowProps {
  context: ContextLineView;
  /** The character's message, for the replay clip. Without it no replay is offered. */
  audio?: Message;
}

interface ContextRowsProps {
  contexts: ContextLineView[];
  /** The character's messages, to find each line's replay clip by `messageId`. */
  audios?: Message[];
}

/**
 * The line this turn answers, above the card: what the character said, what it
 * meant, and a replay. Replaying never advances the run, so it passes no
 * `onEnded`.
 */
export const ContextRow: React.FC<ContextRowProps> = ({ context, audio }) => {
  const natural = audio?.audioUrls?.natural;
  const hasText = !!context.targetText || !!context.meaning;
  return (
    <div className={styles.ctx} data-testid="turncard-context">
      {hasText ? (
        <div className={styles.text}>
          <span className={styles.heading}>
            {INTERFACE_TEXT.tcContextHeading}
          </span>
          {context.targetText && (
            <span className={styles.target}>{context.targetText}</span>
          )}
          {context.meaning && (
            <span className={styles.meaning}>{context.meaning}</span>
          )}
          {context.note && (
            <span className={styles.meaning}>{context.note}</span>
          )}
        </div>
      ) : (
        <span className={styles.heading}>{INTERFACE_TEXT.tcContextSpoke}</span>
      )}
      {natural && (
        <div
          className={styles.replay}
          role="group"
          aria-label={INTERFACE_TEXT.tcReplayGroup}
        >
          <AudioPlayer audioUrl={natural} />
        </div>
      )}
    </div>
  );
};

/** Every line the character spoke before this turn, in order, each in its own row (BR-042). */
export const ContextRows: React.FC<ContextRowsProps> = ({
  contexts,
  audios,
}) => (
  <>
    {contexts.map((context) => (
      <ContextRow
        key={context.messageId}
        context={context}
        audio={audios?.find((m) => m.id === context.messageId)}
      />
    ))}
  </>
);

export default ContextRow;
